"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

export default function OwnerHero() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll(".hero-item").forEach((item, i) => {
      (item as HTMLElement).style.animation = `fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) ${0.1 + i * 0.12}s forwards`;
      (item as HTMLElement).style.opacity = "0";
    });
  }, []);

  return (
    <section
      ref={ref}
      className="relative min-h-[80vh] flex items-center overflow-hidden pt-20 dark:bg-[#0f172a]"
      style={{ background: "linear-gradient(145deg, #E8F8F7 0%, #F0FAFB 50%, #EEF2FF 100%)" }}>
      
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none opacity-40"
        style={{ background: "radial-gradient(circle, rgba(14,165,160,0.12) 0%, transparent 70%)" }} />
      

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 w-full">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="flex flex-col gap-6">
            <div className="hero-item">
              <span className="teal-badge">
                <Icon name="HeartIcon" size={12} variant="solid" />
                For Pet Owners
              </span>
            </div>
            <h1 className="hero-item text-5xl sm:text-[58px] font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight leading-[1.07]">
              Your pet's life,{" "}
              <span className="gradient-text">organized and safe.</span>
            </h1>
            <p className="hero-item text-lg text-[#4A6363] dark:text-slate-400 leading-relaxed max-w-[500px]">
              One profile holds everything — health records, GPS location, vet contacts, reminders, and a trusted circle of people who care for your pet.
            </p>
            <div className="hero-item flex flex-col sm:flex-row gap-3">
              <Link href="#signup" className="btn-primary text-base py-3.5 px-7 transition-all duration-200 hover:scale-105 hover:shadow-lg">
                <Icon name="UserPlusIcon" size={18} />
                Create Free Pet Profile
              </Link>
              <Link href="#features" className="btn-secondary text-base py-3.5 px-7 transition-all duration-200 hover:scale-105 hover:shadow-lg">
                See All Features
              </Link>
            </div>
            <div className="hero-item flex items-center gap-2 text-sm text-[#4A6363] dark:text-slate-400">
              <Icon name="LockClosedIcon" size={14} className="text-teal-500" />
              <span>Your data is private by default. You control all sharing.</span>
            </div>
          </div>

          <div className="relative">
            <div className="hero-item rounded-3xl overflow-hidden" style={{ boxShadow: "0 24px 64px rgba(14,165,160,0.2)" }}>
              <AppImage
                src="https://images.unsplash.com/photo-1610580372313-7d63d9151c62"
                alt="Happy dogfob and cat sitting together on a sofa, representing multi-pet management in DogFob"
                width={600}
                height={440}
                className="w-full h-[420px] object-cover"
                priority />
              
            </div>

            {/* Quick stat cards */}
            <div className="absolute top-5 -left-4 sm:-left-8 glass-card rounded-2xl px-4 py-3 animate-float-a" style={{ boxShadow: "0 12px 36px rgba(15,32,39,0.1)" }}>
              <div className="flex items-center gap-2">
                <Icon name="BellAlertIcon" size={16} className="text-teal-500" />
                <div>
                  <p className="text-[10px] text-[#8AA4A4] dark:text-slate-500">Next reminder</p>
                  <p className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">Rabies booster · 3 days</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 right-4 sm:right-0 glass-card rounded-2xl px-4 py-3 animate-float-b" style={{ boxShadow: "0 12px 36px rgba(15,32,39,0.1)" }}>
              <div className="flex items-center gap-2">
                <div className="status-dot" />
                <div>
                  <p className="text-[10px] text-[#8AA4A4] dark:text-slate-500">GPS status</p>
                  <p className="text-sm font-bold text-[#22C55E] dark:text-slate-100 font-heading">Safe at home</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}